/**
 * SummarySection - Category section with header, separator, and card list
 *
 * Uses a table-like layout with fixed column widths for consistent alignment.
 */
import type { ReactElement } from 'react';

import type { Currency, ScryfallCard, TypeCategory } from '@/types/card';

import SummaryCardRow from './SummaryCardRow';

/** Card type alias for summary view (uses full Scryfall data from deck cache) */
export type SummaryCard = ScryfallCard;

export interface SummarySectionCard {
  card: SummaryCard;
  quantity: number;
  /** Card price (single copy) */
  price: number | null;
  isOwned?: boolean;
  /** Pre-computed type category for sorting within groups */
  typeCategory?: TypeCategory;
}

export interface SummarySectionProps {
  /** Section title (e.g., "CREATURES", "LANDS") */
  title: string;
  /** Total card count for this section */
  count: number;
  /** Cards in this section */
  cards: SummarySectionCard[];
  /** Whether to show prices */
  showPrices?: boolean;
  /** Called when a card is clicked */
  onCardClick?: (scryfallId: string) => void;
  /** Hide header (when parent renders its own collapsible header) */
  hideHeader?: boolean;
  /** Currency for price display */
  currency?: Currency;
}

export default function SummarySection({ cards, showPrices = true, onCardClick, currency = 'USD' }: SummarySectionProps): ReactElement {
  return (
    <div className="overflow-hidden">
      {/* Card Rows - table layout for consistent column widths */}
      <table className="w-full border-collapse">
        <colgroup>
          <col className="w-5" /> {/* Quantity */}
          <col /> {/* Name - takes remaining space */}
          <col className="w-24" /> {/* Mana cost */}
          {showPrices && <col className="w-14" />} {/* Price */}
        </colgroup>
        <tbody>
          {cards.map((c) => (
            <SummaryCardRow
              key={c.card.id}
              card={c.card}
              quantity={c.quantity}
              price={c.price}
              showPrice={showPrices}
              isOwned={c.isOwned}
              onCardClick={onCardClick}
              currency={currency}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
